import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Eye, ChevronRight, ChevronLeft, CheckCircle, XCircle, AlertTriangle, Shield, BookOpen, Video, FileText, Camera, Mic, StickyNote, Trash2, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { GetCourseBuilderModulesOutputType } from 'zite-endpoints-sdk';

type Mod = GetCourseBuilderModulesOutputType['modules'][0];
type Course = GetCourseBuilderModulesOutputType['course'];

function PreviewBanner() {
  return (
    <div className="bg-amber-500/10 border-b-2 border-amber-500/30 px-4 py-2 flex items-center justify-center gap-2">
      <Eye className="w-4 h-4 text-amber-600" />
      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">PREVIEW / NO STAFF IMPACT — V2 Interactive Learner</span>
    </div>
  );
}

function StepDots({ steps, current, completed }: { steps: string[]; current: number; completed: Set<string> }) {
  return (
    <div className="flex gap-1.5">
      {steps.map((s, i) => (
        <div key={s} className={`h-2 flex-1 rounded-full transition-colors ${i === current ? 'bg-primary' : completed.has(s) ? 'bg-primary/40' : 'bg-muted'}`} />
      ))}
    </div>
  );
}

function WelcomePreview({ mod }: { mod: Mod }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">{mod.displayLabel}</h2>
      {mod.learningOutcome && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Learning Objectives</p>
          <p className="text-sm text-foreground whitespace-pre-wrap">{mod.learningOutcome}</p>
        </div>
      )}
      {mod.safetyWarnings && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex gap-3">
          <Shield className="w-5 h-5 text-destructive shrink-0" />
          <div><p className="text-xs font-bold text-destructive">Safety Warning</p><p className="text-xs text-foreground mt-1">{mod.safetyWarnings}</p></div>
        </div>
      )}
      <div className="text-xs text-muted-foreground space-y-1">
        {mod.estimatedMinutes > 0 && <p>⏱ Estimated time: ~{mod.estimatedMinutes} min</p>}
        <p>📋 {mod.keyPoints.length} key points · {mod.scenarios.length} scenarios · {mod.questions.length} questions</p>
      </div>
    </div>
  );
}

function WatchPreview({ mod }: { mod: Mod }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Watch Video</h2>
      {mod.videoUrl ? (
        <video controls className="w-full rounded-xl border border-border" preload="metadata">
          <source src={mod.videoUrl} type="video/mp4" />
        </video>
      ) : (
        <div className="aspect-video bg-muted rounded-xl flex items-center justify-center border border-dashed border-border">
          <div className="text-center"><Video className="w-10 h-10 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">Video production outstanding</p></div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Required watch: {mod.videoRequiredPercent || 80}%</p>
    </div>
  );
}

function LearnPreview({ mod }: { mod: Mod }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Key Points ({mod.keyPoints.length})</h2>
      {mod.keyPoints.map(kp => (
        <div key={kp.id} className="border border-border rounded-xl p-4 space-y-2">
          <button onClick={() => setExpanded(expanded === kp.id ? null : kp.id)} className="text-left w-full">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">{kp.title}</p>
              {kp.category !== 'Standard' && <span className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">{kp.category}</span>}
            </div>
          </button>
          <p className="text-sm text-muted-foreground">{kp.content}</p>
          {expanded === kp.id && kp.expandableDetail && (
            <div className="bg-muted/30 rounded-lg p-3 text-xs text-foreground whitespace-pre-wrap">{kp.expandableDetail}</div>
          )}
          {kp.sopLink && <a href={kp.sopLink} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><FileText className="w-3 h-3" /> SOP Link</a>}
        </div>
      ))}
      {mod.commonMistakes && (
        <div className="bg-muted rounded-xl p-4">
          <p className="text-xs font-bold text-muted-foreground uppercase mb-1">⚠ Common Mistakes</p>
          <p className="text-sm text-foreground whitespace-pre-wrap">{mod.commonMistakes}</p>
        </div>
      )}
    </div>
  );
}

function PractisePreview({ mod }: { mod: Mod }) {
  const [selected, setSelected] = useState<Record<string, number>>({});
  const scenarios = useMemo(() => mod.scenarios.map(s => {
    let choices: { label: string; isCorrect: boolean; feedback: string }[] = [];
    try { choices = JSON.parse(s.choicesJson || '[]'); } catch {}
    return { ...s, choices };
  }), [mod.scenarios]);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Practise Scenarios ({scenarios.length})</h2>
      {scenarios.map((sc) => (
        <div key={sc.id} className="border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground">{sc.title}</p>
          <p className="text-xs text-muted-foreground">{sc.contextDescription}</p>
          <div className="space-y-2">
            {sc.choices.map((ch, ci) => {
              const isSelected = selected[sc.id] === ci;
              const showFeedback = selected[sc.id] !== undefined;
              return (
                <button key={ci} onClick={() => setSelected(prev => ({ ...prev, [sc.id]: ci }))}
                  className={`w-full text-left p-3 rounded-lg border-2 text-sm transition-all ${
                    isSelected ? (ch.isCorrect ? 'border-primary bg-primary/10' : 'border-destructive bg-destructive/10') : 'border-border hover:border-primary/30'
                  }`}>
                  <span>{ch.label}</span>
                  {isSelected && showFeedback && <p className="text-xs mt-1 text-muted-foreground italic">{ch.feedback}</p>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── FIX F: CheckPreview reports pass state upward ──
function CheckPreview({ mod, course, onPassChange }: { mod: Mod; course: Course; onPassChange: (passed: boolean) => void }) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const questions = useMemo(() => mod.questions.map(q => {
    let options: { label: string; value: string }[] = [];
    try { options = JSON.parse(q.optionsJson || '[]'); } catch {}
    return { ...q, options };
  }), [mod.questions]);

  const allAnswered = questions.every(q => answers[q.id]);

  const score = useMemo(() => {
    if (!submitted) return null;
    let correct = 0;
    let criticalFail = false;
    for (const q of questions) {
      const isCorrect = answers[q.id] === q.correctAnswer;
      if (isCorrect) correct++;
      if (!isCorrect && q.isCritical && course.criticalQuestionRule) criticalFail = true;
    }
    const pct = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    const passed = pct >= course.defaultPassMark && !criticalFail;
    return { correct, total: questions.length, pct, passed, criticalFail };
  }, [submitted, answers, questions, course]);

  useEffect(() => {
    onPassChange(score?.passed === true);
  }, [score, onPassChange]);

  const handleSubmit = () => setSubmitted(true);
  const handleRetry = () => { setSubmitted(false); setAnswers({}); };

  // If no questions, auto-pass
  useEffect(() => {
    if (questions.length === 0) onPassChange(true);
  }, [questions.length, onPassChange]);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Knowledge Check ({questions.length} questions)</h2>
      {submitted && score && (
        <div className={`p-4 rounded-xl border-2 text-center ${score.passed ? 'border-primary bg-primary/10' : 'border-destructive bg-destructive/10'}`}>
          <p className="text-2xl font-bold">{score.pct}%</p>
          <p className="text-sm font-semibold">{score.passed ? '✓ Passed' : '✗ Not Passed'} ({score.correct}/{score.total})</p>
          {score.criticalFail && <p className="text-xs text-destructive mt-1">Failed on a critical question</p>}
          <p className="text-xs text-muted-foreground mt-1">Pass mark: {course.defaultPassMark}%</p>
          {!score.passed && <Button variant="outline" onClick={handleRetry} className="mt-3 text-xs">Retry</Button>}
        </div>
      )}
      {!submitted && questions.map((q, qi) => (
        <div key={q.id} className="border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-muted-foreground">Q{qi + 1}</span>
            {q.isCritical && <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-bold">CRITICAL</span>}
          </div>
          <p className="text-sm font-medium text-foreground">{q.questionText}</p>
          <div className="space-y-1.5">
            {q.options.map(opt => (
              <button key={opt.value} onClick={() => setAnswers(a => ({ ...a, [q.id]: opt.value }))}
                className={`w-full text-left p-2.5 rounded-lg border text-sm transition-all ${answers[q.id] === opt.value ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:bg-muted/30'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      {!submitted && questions.length > 0 && (
        <Button onClick={handleSubmit} disabled={!allAnswered} className="w-full h-12">
          {allAnswered ? 'Submit Answers' : `Answer all ${questions.length} questions`}
        </Button>
      )}
      {submitted && questions.map((q, qi) => {
        const isCorrect = answers[q.id] === q.correctAnswer;
        return (
          <div key={q.id} className={`border-2 rounded-xl p-4 space-y-1 ${isCorrect ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold">Q{qi + 1}</span>
              {isCorrect ? <CheckCircle className="w-4 h-4 text-primary" /> : <XCircle className="w-4 h-4 text-destructive" />}
              {q.isCritical && <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-bold">CRITICAL</span>}
            </div>
            <p className="text-sm text-foreground">{q.questionText}</p>
            <p className="text-xs text-muted-foreground">Your answer: {q.options.find(o => o.value === answers[q.id])?.label}</p>
            {!isCorrect && <p className="text-xs text-primary">Correct: {q.options.find(o => o.value === q.correctAnswer)?.label}</p>}
            {q.explanation && <p className="text-xs text-muted-foreground italic mt-1">{q.explanation}</p>}
          </div>
        );
      })}
    </div>
  );
}

// ── FIX F: ReadinessPreview reports confirmed state upward ──
function ReadinessPreview({ onConfirmChange }: { onConfirmChange: (confirmed: boolean) => void }) {
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    onConfirmChange(done);
  }, [done, onConfirmChange]);

  if (done) {
    return <div className="bg-primary/10 rounded-xl p-6 text-center"><Shield className="w-12 h-12 text-primary mx-auto mb-3" /><h2 className="text-lg font-bold">Readiness Confirmed (Preview)</h2><p className="text-sm text-muted-foreground mt-1">No data saved.</p></div>;
  }
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Practical Readiness</h2>
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any areas you'd like to practise more?" rows={4} className="text-sm" />
      <label className="flex items-start gap-3 p-4 border border-border rounded-xl cursor-pointer"><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5 w-5 h-5" /><span className="text-sm">I confirm I feel ready for the practical assessment</span></label>
      <Button onClick={() => setDone(true)} disabled={!confirmed} className="w-full h-12">Confirm Readiness (Preview)</Button>
    </div>
  );
}

function SummaryPreview({ mod }: { mod: Mod }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Summary</h2>
      <div className="border border-border rounded-xl p-4 space-y-2">
        <p className="text-sm font-semibold">{mod.displayLabel}</p>
        <p className="text-xs text-muted-foreground">All 7 steps completed in preview mode.</p>
        <p className="text-xs text-muted-foreground">Key points: {mod.keyPoints.length} · Scenarios: {mod.scenarios.length} · Questions: {mod.questions.length}</p>
      </div>
    </div>
  );
}

// ── FIX E: Local evidence item type ──
type LocalEvidenceItem = {
  id: string;
  type: 'photo' | 'video' | 'audio' | 'note';
  file?: File;
  objectUrl?: string;
  noteText?: string;
  criterionIndex?: number;
};

// ── FIX E: Evidence step before PIN ──
function EvidencePreview({ items: practicalItems, onEvidenceReady }: {
  items: { label?: string; criterion?: string; requiresEvidence?: boolean; evidenceRequired?: boolean }[];
  onEvidenceReady: (ready: boolean) => void;
}) {
  const [evidence, setEvidence] = useState<LocalEvidenceItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingType, setPendingType] = useState<'photo' | 'video' | 'audio' | null>(null);
  const [noteText, setNoteText] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [associatingId, setAssociatingId] = useState<string | null>(null);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      evidence.forEach(e => { if (e.objectUrl) URL.revokeObjectURL(e.objectUrl); });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if evidence requirements are met
  useEffect(() => {
    if (evidence.length === 0) { onEvidenceReady(false); return; }
    // Check requiresEvidence criteria
    for (let i = 0; i < practicalItems.length; i++) {
      const item = practicalItems[i];
      if (item.requiresEvidence || item.evidenceRequired) {
        const hasEvidence = evidence.some(e => e.criterionIndex === i);
        if (!hasEvidence) { onEvidenceReady(false); return; }
      }
    }
    onEvidenceReady(true);
  }, [evidence, practicalItems, onEvidenceReady]);

  const addFile = (type: 'photo' | 'video' | 'audio') => {
    setPendingType(type);
    if (fileInputRef.current) {
      fileInputRef.current.accept = type === 'photo' ? 'image/*' : type === 'video' ? 'video/*' : 'audio/*';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingType) return;
    const objectUrl = URL.createObjectURL(file);
    setEvidence(prev => [...prev, { id: crypto.randomUUID(), type: pendingType, file, objectUrl }]);
    setPendingType(null);
    e.target.value = '';
  };

  const addNote = () => {
    if (!noteText.trim()) return;
    setEvidence(prev => [...prev, { id: crypto.randomUUID(), type: 'note', noteText: noteText.trim() }]);
    setNoteText('');
    setShowNoteForm(false);
  };

  const removeItem = (id: string) => {
    setEvidence(prev => {
      const item = prev.find(e => e.id === id);
      if (item?.objectUrl) URL.revokeObjectURL(item.objectUrl);
      return prev.filter(e => e.id !== id);
    });
  };

  const associateCriterion = (evidenceId: string, criterionIdx: number | undefined) => {
    setEvidence(prev => prev.map(e => e.id === evidenceId ? { ...e, criterionIndex: criterionIdx } : e));
    setAssociatingId(null);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Evidence Capture</h2>
      <div className="bg-amber-500/10 rounded-xl p-3 text-xs text-amber-600 font-bold text-center">
        PREVIEW ONLY — NOTHING UPLOADED · All files stay in local memory
      </div>

      {/* Add buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => addFile('photo')} className="h-12 text-xs gap-2"><Camera className="w-4 h-4" /> Photo</Button>
        <Button variant="outline" onClick={() => addFile('video')} className="h-12 text-xs gap-2"><Film className="w-4 h-4" /> Video</Button>
        <Button variant="outline" onClick={() => addFile('audio')} className="h-12 text-xs gap-2"><Mic className="w-4 h-4" /> Audio</Button>
        <Button variant="outline" onClick={() => setShowNoteForm(true)} className="h-12 text-xs gap-2"><StickyNote className="w-4 h-4" /> Note</Button>
      </div>
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />

      {showNoteForm && (
        <div className="border border-border rounded-xl p-3 space-y-2">
          <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Assessor note…" rows={3} className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={addNote} disabled={!noteText.trim()}>Add Note</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowNoteForm(false); setNoteText(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Evidence list */}
      {evidence.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-muted-foreground uppercase">Evidence Items ({evidence.length})</p>
          {evidence.map(item => (
            <div key={item.id} className="border border-border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {item.type === 'photo' && <Camera className="w-4 h-4 text-primary" />}
                  {item.type === 'video' && <Film className="w-4 h-4 text-primary" />}
                  {item.type === 'audio' && <Mic className="w-4 h-4 text-primary" />}
                  {item.type === 'note' && <StickyNote className="w-4 h-4 text-primary" />}
                  <span className="text-xs font-medium text-foreground capitalize">{item.type}</span>
                  {item.file && <span className="text-[10px] text-muted-foreground">{item.file.name}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
              </div>
              {/* Preview */}
              {item.type === 'photo' && item.objectUrl && <img src={item.objectUrl} alt="Evidence" className="w-full max-h-40 object-cover rounded-lg" />}
              {item.type === 'video' && item.objectUrl && <video src={item.objectUrl} controls className="w-full max-h-32 rounded-lg" />}
              {item.type === 'audio' && item.objectUrl && <audio src={item.objectUrl} controls className="w-full" />}
              {item.type === 'note' && <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg">{item.noteText}</p>}
              {/* Criterion association */}
              {practicalItems.length > 0 && (
                <div>
                  {associatingId === item.id ? (
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-muted-foreground">Link to criterion:</p>
                      <button onClick={() => associateCriterion(item.id, undefined)} className="w-full text-left px-2 py-1 text-xs rounded hover:bg-muted/50">None (general)</button>
                      {practicalItems.map((pi, idx) => (
                        <button key={idx} onClick={() => associateCriterion(item.id, idx)}
                          className={`w-full text-left px-2 py-1 text-xs rounded hover:bg-muted/50 ${item.criterionIndex === idx ? 'bg-primary/10 text-primary font-medium' : ''}`}>
                          {idx + 1}. {pi.label || pi.criterion}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button onClick={() => setAssociatingId(item.id)} className="text-[10px] text-primary hover:underline">
                      {item.criterionIndex !== undefined ? `Linked: ${item.criterionIndex + 1}. ${practicalItems[item.criterionIndex]?.label || practicalItems[item.criterionIndex]?.criterion || ''}` : 'Link to criterion…'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {evidence.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">Add at least one evidence item to proceed</p>
      )}

      {/* Required evidence warnings */}
      {practicalItems.some((pi, i) => (pi.requiresEvidence || pi.evidenceRequired) && !evidence.some(e => e.criterionIndex === i)) && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-3 space-y-1">
          <p className="text-xs font-bold text-destructive">Required evidence missing:</p>
          {practicalItems.map((pi, i) => {
            if (!(pi.requiresEvidence || pi.evidenceRequired)) return null;
            const hasIt = evidence.some(e => e.criterionIndex === i);
            if (hasIt) return null;
            return <p key={i} className="text-xs text-destructive">• {i + 1}. {pi.label || pi.criterion}</p>;
          })}
        </div>
      )}
    </div>
  );
}

function ManagerPreview({ mod }: { mod: Mod }) {
  const [scores, setScores] = useState<Record<number, string>>({});
  const [phase, setPhase] = useState<'scoring' | 'evidence' | 'pin' | 'result'>('scoring');
  const [pin, setPin] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [evidenceReady, setEvidenceReady] = useState(false);

  let items: { label?: string; criterion?: string; evidenceRule?: string; requiresEvidence?: boolean; evidenceRequired?: boolean }[] = [];
  try { items = JSON.parse(mod.practicalAssessmentItems || '[]'); } catch {}

  if (items.length === 0) return <div className="text-center py-8"><p className="text-sm text-muted-foreground">No practical assessment criteria configured.</p></div>;

  const allScored = items.every((_, i) => scores[i]);
  const allCompetent = allScored && Object.values(scores).every(s => s === 'competent');

  if (phase === 'result' && outcome) {
    return (
      <div className={`p-6 rounded-xl text-center border-2 ${outcome === 'Approved' ? 'border-primary bg-primary/10' : 'border-destructive bg-destructive/10'}`}>
        {outcome === 'Approved' ? <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" /> : <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-3" />}
        <h2 className="text-lg font-bold">{outcome} (Preview)</h2>
        <p className="text-sm text-muted-foreground mt-1">No data saved. This is an isolated preview simulation.</p>
        <Button variant="outline" onClick={() => { setOutcome(null); setScores({}); setPhase('scoring'); setPin(''); setEvidenceReady(false); }} className="mt-4">Reset Preview</Button>
      </div>
    );
  }

  if (phase === 'pin') {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold">Manager Sign-off Preview</h2>
        <div className="bg-amber-500/10 rounded-xl p-3 text-xs text-amber-600 font-bold text-center">ISOLATED LOCAL STATE — NO BACKEND WRITES</div>
        <p className="text-sm text-muted-foreground">Enter any 4-digit PIN to simulate sign-off:</p>
        <div className="flex gap-3 justify-center">
          <input type="password" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} maxLength={4} inputMode="numeric"
            className="w-32 h-12 text-center text-2xl font-bold border-2 border-border rounded-xl bg-background text-foreground focus:border-primary outline-none" placeholder="····" />
        </div>
        <Button onClick={() => { setOutcome(allCompetent ? 'Approved' : 'Further Training'); setPhase('result'); }} disabled={pin.length !== 4}
          className="w-full h-12">{allCompetent ? 'Submit: Approve' : 'Submit: Further Training'}</Button>
        <Button variant="outline" onClick={() => setPhase('evidence')} className="w-full">Back to Evidence</Button>
      </div>
    );
  }

  if (phase === 'evidence') {
    return (
      <div className="space-y-4">
        <EvidencePreview items={items} onEvidenceReady={setEvidenceReady} />
        <Button onClick={() => setPhase('pin')} disabled={!evidenceReady} className="w-full h-12">
          {evidenceReady ? 'Proceed to PIN Sign-off' : 'Add required evidence to continue'}
        </Button>
        <Button variant="outline" onClick={() => setPhase('scoring')} className="w-full">Back to Scoring</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Manager Assessment Preview</h2>
      <div className="bg-amber-500/10 rounded-xl p-3 text-xs text-amber-600 font-bold text-center">ISOLATED LOCAL STATE — NO BACKEND WRITES</div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="border border-border rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-muted text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <p className="text-sm font-medium text-foreground">{item.label || item.criterion}</p>
              {(item.requiresEvidence || item.evidenceRequired) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-600 font-bold">Evidence Req</span>}
            </div>
            <div className="flex gap-2">
              {['competent', 'not_yet', 'not_observed'].map(s => (
                <button key={s} onClick={() => setScores(prev => ({ ...prev, [i]: s }))}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    scores[i] === s ? (s === 'competent' ? 'border-primary bg-primary/10 text-primary' : s === 'not_yet' ? 'border-destructive bg-destructive/10 text-destructive' : 'border-muted-foreground bg-muted') : 'border-border'
                  }`}>
                  {s === 'competent' ? '✓ Competent' : s === 'not_yet' ? '✗ Not Yet' : '○ Not Observed'}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Button onClick={() => setPhase('evidence')} disabled={!allScored} className="w-full h-12">
        {allScored ? 'Proceed to Evidence' : `Score all ${items.length} criteria`}
      </Button>
    </div>
  );
}

const STEP_LABELS: Record<string, string> = { welcome: '1. Welcome', watch: '2. Watch', learn: '3. Learn', practise: '4. Practise', check: '5. Check', readiness: '6. Readiness', summary: '7. Summary', manager: '8. Manager Preview' };

export default function V2PreviewPlayer({ modules, course, onBack }: { modules: Mod[]; course: Course; onBack: () => void }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const mod = modules[selectedIdx];
  const enabledSteps = (mod?.stepsEnabled || 'welcome,watch,learn,practise,check,readiness,summary').split(',').map(s => s.trim()).filter(Boolean);
  const steps = [...enabledSteps, 'manager'];
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const stepName = steps[currentStep] || 'welcome';

  // ── FIX F: Track check pass and readiness confirm ──
  const [checkPassed, setCheckPassed] = useState(false);
  const [readinessConfirmed, setReadinessConfirmed] = useState(false);

  const handleCheckPass = useCallback((passed: boolean) => setCheckPassed(passed), []);
  const handleReadinessConfirm = useCallback((confirmed: boolean) => setReadinessConfirmed(confirmed), []);

  const canContinue = (() => {
    if (stepName === 'check') return checkPassed;
    if (stepName === 'readiness') return readinessConfirmed;
    return true;
  })();

  const goNext = () => {
    if (!canContinue) return;
    setCompleted(prev => { const n = new Set(prev); n.add(stepName); return n; });
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goPrev = () => { setCurrentStep(prev => Math.max(0, prev - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const switchModule = (i: number) => {
    setSelectedIdx(i);
    setCurrentStep(0);
    setCompleted(new Set());
    setCheckPassed(false);
    setReadinessConfirmed(false);
  };

  if (!mod) return null;

  return (
    <div className="max-w-lg mx-auto pb-24">
      <PreviewBanner />
      <div className="px-4 py-3 border-b border-border bg-card">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="w-4 h-4" /> Back to Builder
        </button>
        <h1 className="text-base font-bold text-foreground">{course.title}</h1>
        <div className="flex gap-1.5 overflow-x-auto mt-2 pb-1">
          {modules.map((m, i) => (
            <button key={m.id} onClick={() => switchModule(i)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                i === selectedIdx ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'
              }`}>
              {m.displayOrder}. {m.displayLabel}
            </button>
          ))}
        </div>
        <div className="mt-2">
          <StepDots steps={steps} current={currentStep} completed={completed} />
          <p className="text-[10px] text-muted-foreground mt-1 text-center">{STEP_LABELS[stepName] || stepName}</p>
        </div>
      </div>

      <div className="px-4 py-4">
        {stepName === 'welcome' && <WelcomePreview mod={mod} />}
        {stepName === 'watch' && <WatchPreview mod={mod} />}
        {stepName === 'learn' && <LearnPreview mod={mod} />}
        {stepName === 'practise' && <PractisePreview mod={mod} />}
        {stepName === 'check' && <CheckPreview mod={mod} course={course} onPassChange={handleCheckPass} />}
        {stepName === 'readiness' && <ReadinessPreview onConfirmChange={handleReadinessConfirm} />}
        {stepName === 'summary' && <SummaryPreview mod={mod} />}
        {stepName === 'manager' && <ManagerPreview mod={mod} />}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex gap-3 z-50 max-w-lg mx-auto">
        <Button variant="outline" onClick={currentStep === 0 ? onBack : goPrev} className="flex-1 h-12 touch-manipulation">
          <ChevronLeft className="w-4 h-4 mr-1" /> {currentStep === 0 ? 'Builder' : 'Back'}
        </Button>
        {currentStep < steps.length - 1 ? (
          <Button onClick={goNext} disabled={!canContinue} className="flex-1 h-12 touch-manipulation">
            {canContinue ? 'Continue' : (stepName === 'check' ? 'Pass the check first' : 'Confirm readiness first')} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={onBack} className="flex-1 h-12 touch-manipulation">
            Back to Builder <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
